import { dateTimeUtils } from "cayo.ui";
import { AvailabilitySchedule, DayOfWeek } from "@cayo/shared/api/types/cayo-graph";
import { TimeIntervalSelectionMode } from "../../api/schema.api";
import logger from "@cayo/shared/libs/logger";
import calendarUtils from "../../utils/calendar-utils";
import numberUtils from "../../utils/number-utils";
import { objectUtils } from "@cayo/shared/utils/object";
import { EditModes, IntermittentTimeWindow, SelectedHour, TimeWindow } from "./types";

const resetTimeLimits = () => Array<number>(24).fill(0);

const beginSelection = (current: TimeWindow[], week: number, hour: number, editMode: EditModes) => {
  const window = current[week];
  // for (var i = 0; i < window.timeLimit.length; i++) {
  //   window.timeLimit[i] = 0;
  // }

  window.timeLimit[hour] = editMode === "allowed" ? 1 : 0;
};

const moveSelection = (
  current: TimeWindow[],
  last: TimeWindow[],
  beginHour: SelectedHour,
  endHour: SelectedHour,
  editMode: EditModes
) => {
  const realHourBegin = Math.min(beginHour.hour, endHour.hour);
  const realWeekBegin = Math.min(beginHour.week, endHour.week);
  const realHourEnd = Math.max(beginHour.hour, endHour.hour);
  const realWeekEnd = Math.max(beginHour.week, endHour.week);
  logger.debug("moveSelection", realWeekBegin, realHourBegin, realWeekEnd, realHourEnd);

  const setValue: number = editMode === "allowed" ? 1 : 0;
  for (let week = 0; week < current.length; week++) {
    for (let hour = 0; hour < current[week].timeLimit.length; hour++) {
      if (
        hour >= realHourBegin &&
        week >= realWeekBegin &&
        hour <= realHourEnd &&
        week <= realWeekEnd
      ) {
        current[week].timeLimit[hour] = setValue;
      } else {
        current[week].timeLimit[hour] = last[week].timeLimit[hour];
      }
    }
  }
};

const adjustSelection = (current: TimeWindow[]) => {
  for (let week = 0; week < current.length; week++) {
    const firstSet = current[week].timeLimit.findIndex((t) => t === 1);
    const revertedWeek = [...current[week].timeLimit].reverse();
    const lastSet = current[week].timeLimit.length - 1 - revertedWeek.findIndex((t) => !!t);

    if (firstSet >= 0 && firstSet !== lastSet) {
      for (let i = firstSet; i <= lastSet; i++) {
        current[week].timeLimit[i] = 1;
      }
    }
  }
};

const initIntermittentTimeWindows = (
  weekDays: string[],
  value?: IntermittentTimeWindow[]
): TimeWindow[] => {
  if (value) {
    const newTimeWindows = value?.map((timeWindow, i) => {
      const weekDay = weekDays.find((weekDay) => weekDay.startsWith(timeWindow.Day));

      const sortedTimeLimits = [];
      for (let i = 0; i < Object.keys(timeWindow).length; i++) {
        const hour = timeWindow[`h${i + 1}`];
        if (typeof hour === "boolean") {
          sortedTimeLimits.push(Number(hour));
        }
      }

      return {
        weekDay: weekDay || weekDays[i],
        timeLimit: sortedTimeLimits,
      };
    });

    return newTimeWindows;
  } else {
    return weekDays.map((weekDay) => ({ weekDay, timeLimit: resetTimeLimits() }));
  }
};

const initTimeWindows = (weekDays: string[], value?: AvailabilitySchedule[]) => {
  const result: TimeWindow[] = [];

  weekDays.forEach((weekDay) => {
    const timeLimit = resetTimeLimits();
    const lowWeekDay = weekDay.toLocaleLowerCase();
    const schedule = value?.find((s) => s?.days?.find((d) => d.toLocaleLowerCase() === lowWeekDay));
    if (schedule) {
      const beginHour = dateTimeUtils.parseTime(schedule.startTime, false)?.value?.getHours();
      const endHour = dateTimeUtils.parseTime(schedule.endTime, false)?.value?.getHours() ?? 24;

      if (numberUtils.between(0, 23, beginHour)) {
        timeLimit[beginHour!] = 1;
      }

      if (endHour === 24 || numberUtils.between(0, 23, endHour)) {
        timeLimit[endHour! - 1] = 1;
      }
      logger.debug(beginHour, endHour);
    }

    result.push({ weekDay, timeLimit });
  });

  timeWindowsUtils.adjustSelection(result);

  return result;
};

const convertToAvailabilitySchedule = (
  value: TimeWindow[],
  selectionMode: TimeIntervalSelectionMode
) => {
  if (selectionMode === TimeIntervalSelectionMode.Intermittent) {
    const intermittentTimeWindows = value.map((timeWindow) => {
      const intermittentObject: IntermittentTimeWindow = { Day: "" };
      const { timeLimit, weekDay } = timeWindow;
      intermittentObject.Day = weekDay.slice(0, 2);
      timeLimit.forEach((t, i) => {
        intermittentObject[`h${i + 1}`] = Boolean(t);
      });

      return intermittentObject;
    });

    return intermittentTimeWindows;
  } else {
    const result: AvailabilitySchedule[] = [];

    const aux = value
      .map((v) => {
        const t = v.timeLimit;
        const firstSet = t.findIndex((t) => t === 1);
        const revertedWeek = [...t].reverse();
        let lastSet = t.length - 1 - revertedWeek.findIndex((t) => !!t);

        if (firstSet < 0 && lastSet > 23) {
          return undefined;
        }

        lastSet = lastSet + 1;

        return {
          range: `${firstSet}_${lastSet}`,
          weekDay: v.weekDay.toLowerCase(),
          beginHour: firstSet,
          endHour: lastSet,
        };
      })
      .filter((v) => !!v);
    const groups = objectUtils.groupBy(aux, (o) => o.range);

    logger.debug("groups", groups);
    for (const group of groups) {
      const item = group[1];
      const weekDays = (item as any[]).map((i) => i.weekDay as string) as DayOfWeek[];
      const startTime = item[0].beginHour;
      const endTime = item[0].endHour;
      logger.debug(group[1]);

      const endTimeStr = endTime === 24 ? undefined : `${endTime}:00:00`; //endTime === 24 ? "23:59:00" :

      result.push({ days: weekDays, startTime: `${startTime}:00:00`, endTime: endTimeStr });
    }

    return result;
  }
  // for (const group of groups) {
  //   const schedule:AvailabilitySchedule = {days: group}
  // }
};

const setNineToFiveSelection = (timeWindows: TimeWindow[], locale: string): TimeWindow[] => {
  const nineToFiveHours = timeWindows.map((timeWindow) => {
    const weekend = calendarUtils.getWeekends(locale);
    if (weekend.includes(timeWindow.weekDay)) {
      return { weekDay: timeWindow.weekDay, timeLimit: resetTimeLimits() };
    } else {
      const newTimeLimits = timeWindow.timeLimit.map((hour, i) =>
        numberUtils.between(9, 16, i) ? 1 : 0
      );
      return { weekDay: timeWindow.weekDay, timeLimit: newTimeLimits };
    }
  });
  return nineToFiveHours;
};

const timeWindowsUtils = {
  adjustSelection,
  moveSelection,
  beginSelection,
  initTimeWindows,
  initIntermittentTimeWindows,
  convertToAvailabilitySchedule,
  setNineToFiveSelection,
};

export default timeWindowsUtils;
